// PDFViewer2.js
import React from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { Modal, Spinner } from "react-bootstrap";
import { FiChevronLeft, FiChevronRight, FiZoomIn, FiZoomOut, FiDownload } from "react-icons/fi";
// import usePersistScreenshotBlackout from "./DisableScreenShot/disablescreenshot";

try {
  pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;
} catch (error) {
  console.error('Failed to load PDF worker:', error);
  pdfjs.GlobalWorkerOptions.workerSrc = require('pdfjs-dist/build/pdf.worker.min.js');
}

const PDFViewer2 = ({ pdfUrl, show, onHide }) => {
  const [numPages, setNumPages] = React.useState(null);
  const [pageNumber, setPageNumber] = React.useState(1);
  const [scale, setScale] = React.useState(1.0);
  const modalBodyRef = React.useRef(null);
  const pageContainerRef = React.useRef(null);
  // usePersistScreenshotBlackout();

  function onDocumentLoadSuccess({ numPages }) {
    setNumPages(numPages);
    setPageNumber(1);
  }

  const changePage = (offset) => {
    setPageNumber(prevPageNumber => {
      const newPageNumber = prevPageNumber + offset;
      if (newPageNumber < 1 || newPageNumber > numPages) {
        return prevPageNumber;
      }
      return newPageNumber;
    });
  };

  const zoomIn = () => {
    setScale(prevScale => Math.min(prevScale + 0.25, 2.5));
  };

  const zoomOut = () => {
    setScale(prevScale => Math.max(prevScale - 0.25, 0.5));
  };

  // Center the page after zoom or page change
  React.useEffect(() => {
    if (modalBodyRef.current && pageContainerRef.current) {
      const container = modalBodyRef.current;
      const pageWidth = pageContainerRef.current.scrollWidth;
      const containerWidth = container.clientWidth;
      
      const maxScrollLeft = pageWidth - containerWidth;
      
      if (pageWidth > containerWidth) {
        container.scrollTo({
          left: maxScrollLeft / 2,
          behavior: 'auto'
        });
      } else {
        container.scrollTo({
          left: 0,
          behavior: 'auto'
        });
      }
      
      container.scrollTo({
        top: 0,
        behavior: 'auto'
      });
    }
  }, [scale, pageNumber]);

  return (
    <Modal 
      show={show} 
      onHide={onHide} 
      fullscreen 
      centered 
      style={{
        zIndex: 1060
      }}
    >
      {/* Combined Header and Controls in one sticky container */}
      <div style={{
        position: 'sticky',
        top: 0,
        zIndex: 3,
        backgroundColor: '#0d6efd',
        color: 'white'
      }}>
        <Modal.Header 
          closeButton 
          style={{
            borderBottom: 'none',
            paddingBottom: '0',
            backgroundColor: 'inherit',
            color: 'inherit'
          }}
        >
          <Modal.Title style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            paddingRight: '1rem'
          }}>PDF Viewer</Modal.Title>
        </Modal.Header>

        {/* Controls Bar - now part of the sticky header */}
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '0.5rem 1rem',
          backgroundColor: '#f8f9fa',
          borderBottom: '1px solid #dee2e6',
          boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: '0.5rem'
          }}>
            <button
              style={{
                padding: '0.25rem 0.5rem',
                fontSize: '0.875rem',
                borderRadius: '0.2rem',
                border: '1px solid #0d6efd',
                backgroundColor: 'transparent',
                color: '#0d6efd',
                marginRight: '0.5rem'
              }}
              onClick={() => changePage(-1)}
              disabled={pageNumber <= 1}
              aria-label="Previous page"
            >
              <FiChevronLeft />
            </button>

            <span style={{
              whiteSpace: 'nowrap',
              margin: '0 0.25rem',
              fontSize: '0.875rem',
              color: '#212529'
            }}>
              Page <strong>{pageNumber}</strong> of <strong>{numPages || '--'}</strong>
            </span>

            <button
              style={{
                padding: '0.25rem 0.5rem',
                fontSize: '0.875rem',
                borderRadius: '0.2rem',
                border: '1px solid #0d6efd',
                backgroundColor: 'transparent',
                color: '#0d6efd',
                marginLeft: '0.5rem'
              }}
              onClick={() => changePage(1)}
              disabled={pageNumber >= (numPages || 0)}
              aria-label="Next page"
            >
              <FiChevronRight />
            </button>
          </div>

          <div style={{
            display: 'flex',
            alignItems: 'center'
          }}>
            <button
              style={{
                padding: '0.25rem 0.5rem',
                fontSize: '0.875rem',
                borderRadius: '0.2rem',
                border: '1px solid #6c757d',
                backgroundColor: 'transparent',
                color: '#6c757d',
                marginRight: '0.5rem'
              }}
              onClick={zoomOut}
              disabled={scale <= 0.5}
              aria-label="Zoom out"
            >
              <FiZoomOut />
            </button>

            <span style={{
              margin: '0 0.25rem',
              fontSize: '0.875rem',
              color: '#6c757d'
            }}>
              {Math.round(scale * 100)}%
            </span>

            <button
              style={{
                padding: '0.25rem 0.5rem',
                fontSize: '0.875rem',
                borderRadius: '0.2rem',
                border: '1px solid #6c757d',
                backgroundColor: 'transparent',
                color: '#6c757d',
                marginLeft: '0.5rem'
              }}
              onClick={zoomIn}
              disabled={scale >= 2.5}
              aria-label="Zoom in"
            >
              <FiZoomIn />
            </button>
          </div>
        </div>
      </div>

      {/* Scrollable PDF Container */}
      <Modal.Body 
        ref={modalBodyRef}
        style={{
          padding: '20px 0',
          overflow: 'auto',
          height: 'calc(100vh - 120px)', // Adjusted for combined header height
          backgroundColor: '#525659',
          display: 'flex',
          justifyContent: 'center'
        }}
      >
        <div 
          ref={pageContainerRef}
          style={{
            padding: '0 20px',
            display: 'inline-block',
            minWidth: '100%',
            textAlign: 'center'
          }}
        >
          <Document
            file={pdfUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            loading={
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%'
              }}>
                <div style={{
                  textAlign: 'center',
                  padding: '1rem'
                }}>
                  <Spinner animation="border" variant="primary" />
                  <p style={{ marginTop: '0.5rem' }}>Loading PDF document...</p>
                </div>
              </div>
            }
            error={
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%'
              }}>
                <div style={{
                  textAlign: 'center',
                  padding: '1rem',
                  color: '#dc3545'
                }}>
                  <p>Failed to load PDF</p>
                  {/* <button
                    style={{
                      padding: '0.25rem 0.5rem',
                      fontSize: '0.875rem',
                      borderRadius: '0.2rem',
                      border: '1px solid #0d6efd',
                      backgroundColor: '#0d6efd',
                      color: 'white',
                      marginTop: '0.5rem'
                    }}
                    onClick={() => window.open(pdfUrl, '_blank')}
                  >
                    <FiDownload style={{ marginRight: '0.25rem' }} />
                    Open in new tab
                  </button> */}
                </div>
              </div>
            }
          >
            <Page
              pageNumber={pageNumber}
              scale={scale}
              renderTextLayer={false}
              renderAnnotationLayer={false}
              loading={
                <div style={{
                  textAlign: 'center',
                  padding: '1rem'
                }}>
                  <Spinner animation="border" variant="primary" />
                  <p style={{ marginTop: '0.5rem' }}>Loading page...</p>
                </div>
              }
              style={{
                display: 'inline-block',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}
              width={Math.min(800 * scale, window.innerWidth * 0.9)}
            />
          </Document>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default PDFViewer2;